import React, { useState } from "react";
import { AutoComplete, Button, DatePicker, Input, InputNumber, Spin, Upload, message } from "antd";
import { CloudUploadOutlined, DeleteOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import {
  blankEstimate,
  estimateGaps,
  extractClaimForm,
} from "./estimateClaimsApi";
import EstimateProcedures from "./EstimateProcedures";
import "./estimateClaims.css";

/**
 * The estimates an estimate request is asking us to chase, per policy.
 *
 * A breakdown needs only a policy; an estimate has to name a specific
 * submission, or the FTC user opens the task with nothing to go on. So for each
 * selected policy the office lists one or more estimates — amount, date of
 * service, patient, provider and the CDA/iTrans number.
 *
 * Uploading the claim form is a SHORTCUT, not a requirement. Drop it in and the
 * server reads those fields off it for review; skip it and type them. Both paths
 * end in the same reviewed values, which is why the fields are always visible and
 * editable rather than appearing only after an upload.
 *
 * `PolicyEstimates` is ONE policy's worth of rows, which the Insurance
 * Verification composer wraps in its save-as-you-go checklist (EstimateChecklist).
 * It is the only layout: the stacked all-policies editor that the old
 * RequestVerificationPolicyModal used went with that modal.
 */
/** Normalise to a list that always has at least one row to type into. */
export function estimateRows(estimates, cdaItrans) {
  return estimates && estimates.length ? estimates : [blankEstimate(cdaItrans)];
}

/**
 * One policy's estimates: the upload, the fields, and "add another".
 *
 * Exported so a host can lay the policies out however it likes — stacked, or as
 * accordion cards — without re-implementing the form or the extraction flow.
 *
 * @param {Array}    estimates  this policy's rows (a blank one is shown if empty)
 * @param {Function} onChange   (nextRows) => void
 */
export function PolicyEstimates({
  patientId,
  estimates,
  onChange,
  providerOptions,
  disabled,
  // Left undefined the popups portal to document.body, which is correct inside a
  // Modal. A Drawer host passes trigger.parentElement instead, or the calendar
  // opens behind the drawer and can never be clicked.
  getPopupContainer,
  // The flat layout has no other feedback, so it states the gap under the fields.
  // A host that already says it elsewhere (the checklist says it in the card
  // header AND beside the Save button) turns this off rather than saying it three
  // times in one card.
  showGapNote = true,
  // The office's saved CDA/iTrans number. Pre-fills every new row, because it is
  // the same on every claim the office sends.
  cdaItrans,
  // What one row is called: "estimate" (default), or "claim" when the host is a
  // claim follow-up — the same fields, chased for a different reason.
  noun = "estimate",
}) {
  // index -> true while that row's form is being read
  const [extracting, setExtracting] = useState({});
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

  const rows = estimateRows(estimates, cdaItrans);

  function update(index, patch) {
    onChange(
      rows.map(function (est, i) {
        return i === index ? Object.assign({}, est, patch) : est;
      }),
    );
  }

  function add() {
    onChange(rows.concat([blankEstimate(cdaItrans)]));
  }

  function remove(index) {
    const next = rows.filter(function (_e, i) { return i !== index; });
    onChange(next.length ? next : [blankEstimate(cdaItrans)]);
  }

  function setBusy(index, busy) {
    setExtracting(function (prev) {
      return Object.assign({}, prev, { [index]: busy });
    });
  }

  /**
   * Read a dropped claim form and fill what it found.
   *
   * Only fills fields the extractor actually returned — anything the user has
   * already typed for a field the model couldn't read survives, because their
   * value is better than a blank.
   */
  async function handleFile(index, file) {
    setBusy(index, true);
    try {
      const res = await extractClaimForm(patientId, file);
      if (!res || res.status >= 400) {
        const detail = (res && res.json && res.json.detail) || "Couldn't read that file.";
        message.error(detail);
        return;
      }
      const body = res.json || {};
      const got = body.extracted || {};
      const patch = { claim_form_path: body.claim_form_path, claim_form_name: file.name };
      ["amount", "service_date", "patient_name", "provider_name_raw", "cda_itrans_number"].forEach(
        function (field) {
          const v = got[field];
          if (v !== null && v !== undefined && String(v) !== "") {
            patch[field] = field === "service_date" ? dayjs(v) : v;
          }
        },
      );
      // The procedure lines too — but never over ones the user already picked.
      const current = rows[index] || {};
      if ((body.procedures || []).length && !(current.procedures || []).length) {
        patch.procedures = body.procedures;
      }
      update(index, patch);

      if (body.extraction_empty) {
        // Say so plainly rather than leaving them staring at an unchanged form.
        message.info("We couldn't read that form — please enter the details below.");
      } else {
        message.success("Details read from the form — please check them.");
      }
    } catch (e) {
      console.error(e);
      message.error("Couldn't read that file. Enter the details below instead.");
    } finally {
      setBusy(index, false);
    }
  }

  return (
    <>
      {rows.map(function (est, index) {
        const busy = !!extracting[index];
        // With a form attached nothing is a gap: the form carries the details.
        const missing = estimateGaps(est);
        return (
          <div key={index} className="est-claim">
            <div className="est-claim-head">
              <span className="est-claim-title">
                {Noun} {rows.length > 1 ? index + 1 : ""}
              </span>
              {rows.length > 1 ? (
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={disabled || busy}
                  onClick={function () { remove(index); }}
                  aria-label={"Remove this " + noun}
                />
              ) : null}
            </div>

            {/* Upload first: it fills the fields below, so it reads as the
                shortcut it is rather than an afterthought. */}
            <Spin spinning={busy} tip="Reading the form…">
              {est.claim_form_path ? (
                <div className="est-claim-file">
                  <FileTextOutlined />
                  <span className="est-claim-file-name">{est.claim_form_name || "Claim form"}</span>
                  <Button
                    type="link"
                    size="small"
                    disabled={disabled || busy}
                    onClick={function () {
                      update(index, { claim_form_path: null, claim_form_name: null });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Upload.Dragger
                  multiple={false}
                  showUploadList={false}
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.heic"
                  disabled={disabled || busy}
                  beforeUpload={function (file) {
                    handleFile(index, file);
                    return false;   // we upload it ourselves, to the extract endpoint
                  }}
                  className="est-claim-drop"
                >
                  <div className="est-claim-drop-icon"><CloudUploadOutlined /></div>
                  <p className="est-claim-drop-text">
                    Upload the claim form
                  </p>
                  <p className="est-claim-drop-hint">
                    Drop a PDF or photo here, or <span>browse</span> — we'll fill in the
                    details below for you to check.
                  </p>
                </Upload.Dragger>
              )}
            </Spin>

            <div className="est-claim-or">
              {est.claim_form_path
                ? "What we read — check it; anything we couldn't read can stay blank"
                : "or enter them yourself"}
            </div>

            <div className="est-claim-grid">
              <label className="est-field">
                <span className="est-field-label">Amount</span>
                <InputNumber
                  value={est.amount === "" || est.amount === null ? null : Number(est.amount)}
                  onChange={function (v) { update(index, { amount: v === null ? "" : String(v) }); }}
                  disabled={disabled || busy}
                  prefix="$"
                  min={0}
                  precision={2}
                  style={{ width: "100%" }}
                  placeholder="0.00"
                />
              </label>

              <label className="est-field">
                <span className="est-field-label">Date of service</span>
                <DatePicker
                  value={est.service_date ? dayjs(est.service_date) : null}
                  onChange={function (d) { update(index, { service_date: d }); }}
                  disabled={disabled || busy}
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                  placeholder="Select date"
                  getPopupContainer={getPopupContainer}
                />
              </label>

              <label className="est-field">
                <span className="est-field-label">Patient name</span>
                <Input
                  value={est.patient_name}
                  onChange={function (e) { update(index, { patient_name: e.target.value }); }}
                  disabled={disabled || busy}
                  placeholder="As printed on the form"
                />
              </label>

              <label className="est-field">
                <span className="est-field-label">Provider</span>
                {/* AutoComplete, not Select: the form may name someone who
                    isn't on file, and refusing that would block the request. */}
                <AutoComplete
                  value={est.provider_name_raw}
                  onChange={function (v) { update(index, { provider_name_raw: v }); }}
                  options={providerOptions || []}
                  disabled={disabled || busy}
                  filterOption={function (input, option) {
                    return (option.value || "").toLowerCase().indexOf((input || "").toLowerCase()) >= 0;
                  }}
                  placeholder="Treating provider"
                  style={{ width: "100%" }}
                  getPopupContainer={getPopupContainer}
                />
              </label>

              <label className="est-field est-field--full">
                <span className="est-field-label">CDA / iTrans #</span>
                <Input
                  value={est.cda_itrans_number}
                  onChange={function (e) { update(index, { cda_itrans_number: e.target.value }); }}
                  disabled={disabled || busy}
                  placeholder="Claim reference the carrier tracks this under"
                />
              </label>
            </div>

            <EstimateProcedures
              patientId={patientId}
              value={est.procedures}
              disabled={disabled || busy}
              getPopupContainer={getPopupContainer}
              onChange={function (next) { update(index, { procedures: next }); }}
            />

            {showGapNote && missing.length && !busy ? (
              <div className="est-claim-gap">
                Still needs {missing.length} detail{missing.length > 1 ? "s" : ""} before this can be sent.
              </div>
            ) : null}
          </div>
        );
      })}

      <Button
        type="link"
        icon={<PlusOutlined />}
        disabled={disabled}
        onClick={add}
        className="est-claims-add"
      >
        Add another {noun} for this policy
      </Button>
    </>
  );
}
